import { Component, Input, inject, signal, computed, effect, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EquipementService, EquipementSnapshot, Equipement, SensorDetail, QueueOption, SensorUpsertRequest } from '../../core/services/equipement.service';
import { SessionService } from '../../core/session.service';
import { API_BASE_URL } from '../../core/api-base-url.token';
import { formatLastFrame, lastFrameClass } from '../../core/utils/last-frame.util';
import { CapteurDetailModalComponent } from './capteur-detail-modal.component';
import { CapteurUpsertModalComponent } from './capteur-upsert-modal.component';
import { ConfirmModalComponent } from '../../component/confirm-modal/confirm-modal.component';

@Component({
  selector: 'app-equipements',
  standalone: true,
  imports: [CommonModule, CapteurDetailModalComponent, CapteurUpsertModalComponent, ConfirmModalComponent, TranslatePipe],
  templateUrl: './equipements.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./equipements.component.scss'],
})
export class EquipementsComponent {
  @Input() inline = false;
  @Input() set defaultSiteId(id: string | null) { if (id) this.filterSiteId.set(id); }

  private readonly api        = inject(EquipementService);
  readonly session            = inject(SessionService);
  private readonly http       = inject(HttpClient);
  private readonly baseUrl    = inject(API_BASE_URL);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate  = inject(TranslateService);

  data = signal<EquipementSnapshot | null>(null);
  loading = signal(true);
  error = signal(false);

  selectedDetail = signal<SensorDetail | null>(null);
  detailLoading = signal(false);

  filterSiteId  = signal<string>('');
  filterStatus  = signal<'all' | 'OK' | 'Offline' | 'Instable' | 'batterieFaible'>('all');
  searchQuery   = signal<string>('');
  searchOpen    = signal(false);

  readonly siteStats = computed(() => {
    const d = this.data();
    if (!d) return { total: 0, enLigne: 0, offline: 0, instable: 0, batterieFaible: 0 };
    const siteId = this.filterSiteId();
    const list = siteId ? d.equipements.filter(e => e.establishmentId === siteId) : d.equipements;
    return {
      total:          list.length,
      enLigne:        list.filter(e => e.statut === 'OK').length,
      offline:        list.filter(e => e.statut === 'Offline').length,
      instable:       list.filter(e => e.statut === 'Instable').length,
      batterieFaible: list.filter(e =>
        (e.batterie !== null && e.batterie < 20) ||
        (e.nodeBatterie !== null && e.nodeBatterie < 20)
      ).length,
    };
  });

  readonly filteredEquipements = computed(() => {
    const d = this.data();
    if (!d) return [];
    let list = d.equipements;
    const siteId = this.filterSiteId();
    if (siteId) list = list.filter(e => e.establishmentId === siteId);
    const status = this.filterStatus();
    if (status === 'OK')            list = list.filter(e => e.statut === 'OK');
    else if (status === 'Offline')  list = list.filter(e => e.statut === 'Offline');
    else if (status === 'Instable') list = list.filter(e => e.statut === 'Instable');
    else if (status === 'batterieFaible')
      list = list.filter(e =>
        (e.batterie !== null && e.batterie < 20) ||
        (e.nodeBatterie !== null && e.nodeBatterie < 20)
      );
    const q = this.searchQuery().toLowerCase().trim();
    if (q) list = list.filter(e => e.capteur.toLowerCase().includes(q));
    return list;
  });

  upsertSensor = signal<Equipement | null | 'new'>(null);
  upsertEstablishmentId = signal<string | null>(null);
  queues = signal<QueueOption[]>([]);
  saving = signal(false);
  saveError = signal<string | null>(null);

  deleteTarget = signal<Equipement | null>(null);
  deleteOpen = signal(false);
  deleteLoading = signal(false);
  deleteError = signal<string | null>(null);

  private readonly loadTrigger = new Subject<void>();

  constructor() {
    this.loadTrigger.pipe(
      switchMap(() => {
        this.loading.set(true);
        this.error.set(false);
        return this.api.getOrgSnapshot();
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (d) => { this.data.set(d); this.loading.set(false); },
      error: () => { this.data.set(null); this.loading.set(false); this.error.set(true); },
    });

    effect(() => {
      if (this.session.establishments().length > 0) this.loadTrigger.next();
    });
  }

  private load() {
    this.loadTrigger.next();
  }

  openDetail(e: Equipement) {
    const estId = e.establishmentId ?? this.session.selectedId();
    if (!estId) return;
    this.detailLoading.set(true);
    this.selectedDetail.set(null);
    this.api.getSensorDetail(estId, e.id).subscribe({
      next: (d) => { this.selectedDetail.set(d); this.detailLoading.set(false); },
      error: () => { this.detailLoading.set(false); },
    });
  }

  closeDetail() {
    this.selectedDetail.set(null);
  }

  openUpsert(sensor: Equipement | null) {
    const estId = sensor?.establishmentId
      ?? this.session.selectedId()
      ?? this.session.establishments()[0]?.id
      ?? null;
    if (!estId) return;
    this.upsertEstablishmentId.set(estId);
    this.upsertSensor.set(sensor ?? 'new');
    this.loadQueuesFor(estId);
  }

  onModalEstablishmentChange(estId: string) {
    this.upsertEstablishmentId.set(estId);
    this.loadQueuesFor(estId);
  }

  private loadQueuesFor(estId: string) {
    const base = this.baseUrl.replace(/\/+$/, '');
    this.http.get<{ id: string; name: string }[]>(
      `${base}/api/v1/queues?establishmentId=${encodeURIComponent(estId)}`
    ).subscribe({
      next: (qs) => this.queues.set(qs.map(q => ({ id: q.id, name: q.name }))),
      error: () => this.queues.set([]),
    });
  }

  closeUpsert() {
    this.upsertSensor.set(null);
    this.upsertEstablishmentId.set(null);
    this.saving.set(false);
    this.saveError.set(null);
  }

  onSave(req: SensorUpsertRequest) {
    const estId = this.upsertEstablishmentId();
    if (!estId) return;
    this.saving.set(true);
    this.saveError.set(null);
    const current = this.upsertSensor();
    const isEdit = current !== null && current !== 'new';
    const op$: Observable<unknown> = isEdit
      ? this.api.updateSensor(estId, (current as Equipement).id, req)
      : this.api.createSensor(estId, req);
    op$.subscribe({
      next: () => { this.closeUpsert(); this.load(); },
      error: (err: { error?: { error?: string; message?: string } }) => {
        this.saving.set(false);
        const msg = err?.error?.error ?? err?.error?.message ?? null;
        this.saveError.set(msg ?? 'Une erreur est survenue. Veuillez réessayer.');
      },
    });
  }

  closeSearch() {
    this.searchOpen.set(false);
    this.searchQuery.set('');
  }

  openDelete(e: Equipement) {
    this.deleteTarget.set(e);
    this.deleteOpen.set(true);
    this.deleteError.set(null);
    document.body.style.overflow = 'hidden';
  }

  closeDelete() {
    this.deleteOpen.set(false);
    this.deleteTarget.set(null);
    this.deleteLoading.set(false);
    this.deleteError.set(null);
    document.body.style.overflow = '';
  }

  confirmDelete() {
    const e = this.deleteTarget();
    if (!e) return;
    const estId = e.establishmentId;
    if (!estId) return;
    this.deleteLoading.set(true);
    this.deleteError.set(null);
    this.api.deleteSensor(estId, e.id).subscribe({
      next: () => { this.closeDelete(); this.load(); },
      error: () => {
        this.deleteLoading.set(false);
        this.deleteError.set('Une erreur est survenue. Veuillez réessayer.');
      },
    });
  }

  get upsertSensorForModal(): Equipement | null {
    const v = this.upsertSensor();
    return v === 'new' ? null : v as Equipement | null;
  }

  getStatutClass(s: string): string {
    return s === 'OK' ? 'stable' : s === 'Offline' ? 'critique' : 'moderee';
  }

  getStatutLabelKey(s: string): string {
    return s === 'OK' ? 'equipements.kpi.onlineLabel' : s === 'Offline' ? 'equipements.kpi.offlineLabel' : 'equipements.kpi.unstableLabel';
  }

  getBatterieClass(b: number | null): string {
    if (b === null) return 'bat-inconnu';
    return b >= 80 ? 'bat-ok' : b >= 20 ? 'bat-moyen' : 'bat-faible';
  }

  getDonneeClass(derniereTrameAt: string | null): string {
    return lastFrameClass(derniereTrameAt);
  }

  formatDerniereDonnee(derniereTrameAt: string | null): string {
    return formatLastFrame(derniereTrameAt, this.translate);
  }

  getRssiClass(rssi: number | null): string {
    if (rssi === null) return 'signal-inconnu';
    if (rssi >= -85) return 'signal-ok';
    if (rssi >= -100) return 'signal-moyen';
    return 'signal-faible';
  }

  formatRssi(rssi: number | null): string {
    return rssi !== null ? `${rssi} dBm` : '—';
  }

  formatSnr(snr: number | null): string {
    return snr !== null ? `${snr} dB` : '—';
  }

  formatSf(sf: number | null): string {
    return sf !== null ? `SF${sf}` : '—';
  }

  formatCount(v: number | null): string {
    return v !== null ? v.toString() : '—';
  }

  private hashStr(s: string): number {
    let h = 5381;
    for (let i = 0; i < s.length; i++) h = (((h << 5) + h) ^ s.charCodeAt(i)) >>> 0;
    return h;
  }

  getCount(e: Equipement, field: 'periodicIn' | 'periodicOut' | 'accumulatedIn' | 'accumulatedOut'): string {
    const v = e[field];
    if (v !== null && v > 0) return v.toString();
    if (!e.simulated) return v !== null ? v.toString() : '—';
    const ranges: Record<string, [number, number]> = {
      periodicIn:     [2, 18],
      periodicOut:    [1, 16],
      accumulatedIn:  [30, 180],
      accumulatedOut: [25, 165],
    };
    const [min, max] = ranges[field];
    const h = this.hashStr(e.id + field);
    return String(min + (h % (max - min + 1)));
  }

  getBatterie(e: Equipement): number {
    return e.batterie ?? 0;
  }
}
